import {
  fetchActiveLoanSnapShot,
  fetchLAdditionalInterestSkim,
  fetchAttachments,
  fetchEventJournal,
  fetchLoanCharges,
  fetchOldLoanPortfolio,
  fetchPastPayments,
  fetchPortfolioAtGlance,
  fetchUpcomingPayments,
  fetchPortfolioAtGlanceBroker,
  fetchPortfolioAtGlanceBrokerNew,
  fetchLoanDistByPriorityPie,
  fetchLoanDistByMaturityPie,
  fetchLoanAgingPie,
  fetchLoanDistByPropTypePie,
  fetchLoanDistPrinBalPie,
  fetchWLTVByPropTypePie,
} from "./api";

/**
 * Only the LATEST request of a dashboard table may write its result.
 *
 * Why: a role/account switch aborts in-flight requests (resetController) and
 * resets every table to "idle" (setDashboardStatus). The aborted request then
 * settles -- rejected "canceled", or for loan charges / attachments resolved
 * [] -- and used to land on the NEW cycle: "failed" (endless spinner, no
 * refetch) or an empty "fulfilled" table. Measured on production and UAT,
 * 2026-10-15.
 *
 * The rule is Redux Toolkit's documented createAsyncThunk pattern ("Request
 * user by ID with loading state"): remember meta.requestId on pending, and
 * apply fulfilled/rejected only while the slot is "loading" with that same
 * requestId. Done once around the reducer so the thunks and their case
 * reducers stay exactly as they are.
 *
 * Covers every thunk that writes a slot fetched through useFetchTableData and
 * reset by setDashboardStatus. Keyed by typePrefix, so fetchAttachmentsLender
 * (which shares "dashboard/fetchAttachments") is covered too.
 */
const TABLE_SLOTS = {
  [fetchActiveLoanSnapShot.typePrefix]: "activeLoanSnapShot",
  [fetchLAdditionalInterestSkim.typePrefix]: "additionalInterestSkim",
  [fetchAttachments.typePrefix]: "attachments",
  [fetchEventJournal.typePrefix]: "eventJournal",
  [fetchLoanCharges.typePrefix]: "loanCharges",
  [fetchOldLoanPortfolio.typePrefix]: "oldLoanPortfolio",
  [fetchPastPayments.typePrefix]: "pastPayments",
  [fetchPortfolioAtGlance.typePrefix]: "portfolioAtGlance",
  [fetchUpcomingPayments.typePrefix]: "upcomingPayments",
  [fetchPortfolioAtGlanceBroker.typePrefix]: "portfolioAtGlance",
  [fetchPortfolioAtGlanceBrokerNew.typePrefix]: "portfolioAtGlance",
  [fetchLoanDistByPriorityPie.typePrefix]: "loanDistByPriorityPie",
  [fetchLoanDistByMaturityPie.typePrefix]: "loanDistByMaturityPie",
  [fetchLoanAgingPie.typePrefix]: "loanAgingPie",
  [fetchLoanDistByPropTypePie.typePrefix]: "loanDistByPropTypePie",
  [fetchLoanDistPrinBalPie.typePrefix]: "loanDistPrinBalPie",
  [fetchWLTVByPropTypePie.typePrefix]: "wLTVByPropTypePie",
};

const LIFECYCLE = /^(.*)\/(pending|fulfilled|rejected)$/;

export const latestRequestOnly = (reducer) => (state, action) => {
  const match = typeof action?.type === "string" && LIFECYCLE.exec(action.type);
  const slot = match && TABLE_SLOTS[match[1]];
  const requestId = action?.meta?.requestId;
  if (!slot || !requestId) return reducer(state, action);

  if (match[2] === "pending") {
    const next = reducer(state, action);
    return { ...next, [slot]: { ...next[slot], requestId } };
  }

  const current = state?.[slot];
  const isLatest = current?.status === "loading" && current?.requestId === requestId;
  // A stale result: this request was cancelled by a switch or superseded.
  return isLatest ? reducer(state, action) : state;
};
